import React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button, Space, message, Typography } from "@mimber/ui-web"
import { Form, InputField, DatePickerField } from "@mimber/forms"
import { useUpdateOrganizationProfile } from "@mimber/client/mimber-organization"
import type { OrganizationBasicInfoFormProps } from "./OrganizationBasicInfoForm.types"
import {
  organizationBasicInfoSchema,
  type OrganizationBasicInfoFormData,
} from "./OrganizationBasicInfoForm.schema"

const { Paragraph } = Typography

export const OrganizationBasicInfoForm: React.FunctionComponent<
  OrganizationBasicInfoFormProps
> = ({ organization, onSuccess, disabled = false }) => {
  const updateProfile = useUpdateOrganizationProfile(organization.uuid)

  const form = useForm<OrganizationBasicInfoFormData>({
    defaultValues: {
      aboutText: organization.aboutText || "",
      industry: organization.industry || "",
      foundedDate: organization.foundedDate ?? null,
    },
    resolver: zodResolver(organizationBasicInfoSchema),
    mode: "onBlur",
  })

  const {
    formState: { isDirty },
    reset,
  } = form

  const onSubmit = (data: OrganizationBasicInfoFormData) => {
    const payload = {
      aboutText: data.aboutText || null,
      industry: data.industry || null,
      foundedDate: data.foundedDate ? new Date(data.foundedDate) : null,
    }

    updateProfile.mutate(payload, {
      onSuccess: () => {
        message.success("Basic information updated successfully")
        onSuccess?.()
      },
      onError: (error) => {
        message.error(error.message || "Failed to update basic information")
      },
    })
  }

  const isLoading = updateProfile.isPending

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      disabled={disabled || isLoading}
      layout="vertical"
    >
      <Paragraph type="secondary" style={{ marginBottom: 24 }}>
        Provide essential information about your organization
      </Paragraph>

      <InputField
        name="aboutText"
        label="About Organization"
        placeholder="Tell your members about your organization..."
        multiline
        rows={4}
      />

      <InputField
        name="industry"
        label="Industry"
        placeholder="e.g., Technology, Healthcare, Education"
      />

      <DatePickerField
        name="foundedDate"
        label="Founded Date"
        placeholder="Select date"
      />

      <Space style={{ marginTop: 24 }}>
        <Button
          type="primary"
          htmlType="submit"
          disabled={disabled || !isDirty}
          loading={isLoading}
        >
          Save Changes
        </Button>

        <Button
          onClick={() => reset()}
          disabled={disabled || !isDirty || isLoading}
        >
          Reset
        </Button>
      </Space>
    </Form>
  )
}
